/**
 * Timezone-consistent day membership for meal logs, keyed on `consumedAt`.
 *
 * **The ratified definition: a log belongs to the LOCAL calendar day it reads as
 * on the user's device.**
 *
 * Unlike a workout's `plannedAt`, `consumedAt` has THREE stored shapes:
 *  1. `"2026-03-12"`                 — a bare local date, written by the app
 *                                      (`MealLogScreen`, `MealFavouritesPanel`).
 *  2. `"2026-03-12T00:00:00.000Z"`   — the same value after a Postgres round-trip
 *                                      (the `Timestamptz` column serialises a date
 *                                      to UTC midnight).
 *  3. `"2026-03-12T14:30:00.000Z"`   — a REAL instant, written by the AI worker
 *                                      (`log_meals` declares an ISO 8601 datetime).
 *
 * That third shape is why `PlannedDateRange` cannot be copied verbatim: its
 * `toCalendarDate` is a plain `slice(0, 10)` prefix, which buckets an instant by
 * its **UTC** day rather than the device's local day.
 *
 * With three shapes the local-day rule **cannot be expressed as a single
 * lexicographic SQL range**. So the SQL bounds are a deliberate **superset** —
 * widened by one calendar day on each side, which covers every real UTC offset
 * (max ±14 h) — and the exact decision is made in memory by `containsDay` /
 * `filterToDays`. Over-fetching two extra days from on-device SQLite is
 * negligible (a day is tens of rows).
 *
 * **Known edge — the UTC-midnight heuristic.** A log stored at exactly
 * `T00:00:00.000Z` is indistinguishable from a synced date-only round-trip, so it
 * is treated as date-only. An AI-logged meal at exactly that instant therefore
 * lands one day later, west of UTC, than the instant would imply. This is a
 * deliberate trade: the date-only round-trip is the common case by orders of
 * magnitude, and the alternative silently moves every hand-logged meal for users
 * behind UTC. Removing the ambiguity for good means normalising storage (a
 * date-only `consumedOn` column) — a schema change, out of scope here.
 */
import {
  addDays,
  format,
  isEqual,
  parseISO,
  startOfWeek,
  subDays,
} from 'date-fns';

export interface ConsumedDateRangeBounds {
  /** Inclusive lower bound: a local calendar-date key one day BEFORE the range. */
  fromDate: string;
  /** Inclusive upper bound: one day AFTER the range, at end-of-day. */
  toDate: string;
}

/** The minimum shape day-filtering needs — deliberately not the PowerSync record type. */
export interface ConsumedAtBearing {
  consumedAt: string;
}

export class ConsumedDateRange {
  /** Appended to the upper bound's key so a UTC-midnight `consumedAt` of that day is included. */
  private static readonly END_OF_DAY_SUFFIX = 'T23:59:59.999Z';

  /**
   * How far the SQL bounds are widened on each side. One calendar day covers
   * every real UTC offset (max ±14 h), so no row belonging to the local day can
   * fall outside the query.
   */
  private static readonly WIDEN_DAYS = 1;

  /** The device-LOCAL calendar-date key. */
  private static dateKey(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  /** Widened bounds spanning `[fromDay, toDay]` inclusive, by local calendar date. */
  static forDays(fromDay: Date, toDay: Date): ConsumedDateRangeBounds {
    return {
      fromDate: ConsumedDateRange.dateKey(
        subDays(fromDay, ConsumedDateRange.WIDEN_DAYS),
      ),
      toDate:
        ConsumedDateRange.dateKey(
          addDays(toDay, ConsumedDateRange.WIDEN_DAYS),
        ) + ConsumedDateRange.END_OF_DAY_SUFFIX,
    };
  }

  /** Widened bounds covering a single local calendar day. */
  static forSingleDay(date: Date): ConsumedDateRangeBounds {
    return ConsumedDateRange.forDays(date, date);
  }

  /**
   * The start of `now`'s week under this app's convention (Monday-based).
   *
   * Exposed so a caller that needs the same week window for its in-memory
   * `filterToDays` narrowing does not re-derive `startOfWeek(now, { weekStartsOn:
   * 1 })` locally — the SQL bounds and the narrowing must agree on where a week
   * starts, and two copies of that convention drift.
   */
  static startOfWeekFor(now: Date): Date {
    return startOfWeek(now, { weekStartsOn: 1 });
  }

  /** Widened bounds from the start of `now`'s (Monday-based) week through `now`'s day. */
  static forWeekToDate(now: Date): ConsumedDateRangeBounds {
    return ConsumedDateRange.forDays(
      ConsumedDateRange.startOfWeekFor(now),
      now,
    );
  }

  /**
   * Whether a stored `consumedAt` is a DATE-ONLY value — a bare date, or the
   * exact-UTC-midnight Postgres round-trip of one — rather than a real instant.
   *
   * **The single definition of `consumedAt`'s shape.** `toCalendarDate` branches
   * on it, and so does the copy planner's placement rule, which must classify
   * shape identically or the two would drift apart on the one heuristic DD8
   * flags as this feature's residual ambiguity.
   */
  static isDateOnly(consumedAt: string): boolean {
    if (!consumedAt.includes('T')) return true;
    const instant = parseISO(consumedAt);
    return (
      instant.getUTCHours() === 0 &&
      instant.getUTCMinutes() === 0 &&
      instant.getUTCSeconds() === 0 &&
      instant.getUTCMilliseconds() === 0
    );
  }

  /** The device-LOCAL `yyyy-MM-dd` key for a `Date`. Shared with the copy planner. */
  static toDayKey(date: Date): string {
    return ConsumedDateRange.dateKey(date);
  }

  /**
   * **The authority on which day a log is on.** Shape-aware, NOT a prefix slice.
   *
   * Returns the LOCAL `Date` at midnight of the log's calendar day. Use this for
   * every day grouping, bucketing and labelling of `consumedAt` — a
   * `slice(0, 10)` misbuckets shape 3.
   */
  static toCalendarDate(consumedAt: string): Date {
    // Shapes 1 and 2 — a bare date, or the exact-UTC-midnight round-trip of one.
    // Both are date-only values: shape 1 was authored from the device's local
    // date and carries no zone to convert, and shape 2 MUST agree with it or a
    // log would change day the moment it synced (see the class doc's known edge).
    if (ConsumedDateRange.isDateOnly(consumedAt)) {
      return parseISO(consumedAt.slice(0, 10));
    }

    // Shape 3 — a real instant. Convert to the DEVICE-LOCAL calendar day.
    return parseISO(ConsumedDateRange.dateKey(parseISO(consumedAt)));
  }

  /** Whether `consumedAt`'s local calendar day is `day`'s local calendar day. */
  static containsDay(day: Date, consumedAt: string): boolean {
    return isEqual(
      ConsumedDateRange.toCalendarDate(consumedAt),
      parseISO(ConsumedDateRange.dateKey(day)),
    );
  }

  /**
   * Narrows a widened query result to `[fromDay, toDay]` by LOCAL calendar day.
   * **This, not the SQL bounds, is what the screens filter on.**
   */
  static filterToDays<T extends ConsumedAtBearing>(
    logs: T[],
    fromDay: Date,
    toDay: Date,
  ): T[] {
    const from = parseISO(ConsumedDateRange.dateKey(fromDay));
    const to = parseISO(ConsumedDateRange.dateKey(toDay));
    return logs.filter((log) => {
      const dayOf = ConsumedDateRange.toCalendarDate(log.consumedAt);
      return dayOf >= from && dayOf <= to;
    });
  }
}
